import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "./components/Header";
import { HeroItinerary } from "./components/HeroItinerary";
import { TravelDetailsModal } from "./components/TravelDetailsModal";
import { ItineraryLoading } from "./components/ItineraryLoading";
import { WelcomePromoModal } from "./components/WelcomePromoModal";
import { Footer } from "./components/Footer";
import { AuthCallback } from "./components/AuthCallback";
import { useAuth } from "./contexts/AuthContext";
import { ItineraryProvider, useItinerary } from "./contexts/ItineraryContext";
import { verifyDestinationExists } from "./utils/verifyDestination";
import { FEATURES } from "./config/features";
import { useTranslation } from "react-i18next";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import MyTrips from "./pages/MyTrips";
import Legal from "./pages/Legal";
import RoutePage from "./pages/Route";
import DestinationItinerary from "./pages/DestinationItinerary";
import Destinos from "./pages/Destinos";
import About from "./pages/About";
import Contact from "./pages/Contact";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import { seoItinerariesBySlug } from "./data/seoItineraries";
import { CookieBanner } from "./components/CookieBanner";
import { ScrollToTop } from "./components/ScrollToTop";
import { useAnalytics } from "./hooks/useAnalytics";

const API = process.env.REACT_APP_BACKEND_URL;

function App() {
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ItineraryProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<MainApp />} />
            <Route path="/ruta" element={<RoutePage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/misviajes" element={<MyTrips />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/destinos" element={<Destinos lang="es" />} />
            <Route path="/destinations" element={<Destinos lang="en" />} />
            <Route path="/sobre-nosotros" element={<About lang="es" />} />
            <Route path="/about-us" element={<About lang="en" />} />
            <Route path="/contacto" element={<Contact lang="es" />} />
            <Route path="/contact" element={<Contact lang="en" />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/verificar" element={<VerifyEmail />} />
            <Route path="/recuperar" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {Object.keys(seoItinerariesBySlug).map((slug) => (
              <Route
                key={slug}
                path={`/${slug}`}
                element={<DestinationItinerary data={seoItinerariesBySlug[slug]} />}
              />
            ))}
            <Route path="*" element={<MainApp />} />
          </Routes>
          <CookieBanner />
        </ItineraryProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function MainApp() {
  const { isAuthenticated, user, refresh } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const {
    setItinerary,
    setIsMockMode,
    setSearchParams,
    travelDetails,
    setTravelDetails,
  } = useItinerary();

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSearchData, setCurrentSearchData] = useState({
    startDate: "",
    endDate: "",
    destination: "",
  });

  const userPlan = isAuthenticated && user?.user_plan === "plus" ? "plus" : "basic";

  const handleSearchDataChange = (data) => setCurrentSearchData(data);

  const calculateDays = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24));
  };

  const handleSearch = async (searchData) => {
    setIsLoading(true);
    setSearchParams(searchData);

    // Validamos primero que el destino sea un lugar real (Google Places).
    // Si no, mostramos un mensaje amable y no llamamos a Gemini.
    const lang = (i18n.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
    const exists = await verifyDestinationExists(searchData.destination, lang);
    if (!exists) {
      setIsLoading(false);
      toast.error(
        lang === "en"
          ? "We can't find that place — sounds awesome though! Try another spelling, or write us at info@visitalo.es and we'll add it to the map."
          : "Vaya, ese sitio se nos escapa. Seguro que mola un montón. Prueba a escribirlo de otra forma, o cuéntanoslo a info@visitalo.es y lo metemos en el mapa.",
        { duration: 8000 },
      );
      return;
    }

    let effectivePlan = userPlan;
    try {
      if (isAuthenticated) {
        const token = localStorage.getItem("session_token");
        const { data: consumeResp } = await axios.post(
          `${API}/api/auth/consume-plus-search`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        effectivePlan = consumeResp.effective_plan || "basic";
        if (
          FEATURES.PLUS_ENABLED &&
          effectivePlan === "plus" &&
          !consumeResp.subscription_active &&
          typeof consumeResp.plus_searches_remaining === "number"
        ) {
          const left = consumeResp.plus_searches_remaining;
          if (left > 0) {
            toast.info(`Modo PLUS activado · te quedan ${left} búsquedas gratis`);
          } else {
            toast.info("Esta es tu última búsqueda PLUS gratis. Después pasarás a Basic.");
          }
        }
        refresh && refresh();
      }

      const mergedDetails = searchData._preloadedDetails || travelDetails || {};

      // El modal usa nombres internos (`transportReady`, `departureReady`,
      // `arrivalDateTime`). El backend espera `hasFlights`, `arrivalTime`
      // (HH:MM) y `departureTime` (HH:MM). Aquí hacemos el mapeo para que
      // Gemini reciba las restricciones temporales y NO programe actividades
      // antes de la llegada ni después de la salida.
      const hasFlights = Boolean(
        mergedDetails.transportReady || mergedDetails.departureReady,
      );
      const arrivalTime = mergedDetails.transportReady
        ? mergedDetails.arrivalTime || null
        : null;
      const departureTime = mergedDetails.departureReady
        ? mergedDetails.departureTime || null
        : null;

      const requestData = {
        destination: searchData.destination,
        startDate: searchData.startDate,
        endDate: searchData.endDate,
        userPlan: effectivePlan,
        ...mergedDetails,
        hasFlights,
        arrivalTime,
        departureTime,
      };

      // Forward budget hints to backend if the user picked them in the modal
      if (mergedDetails.budget) {
        requestData.budget = mergedDetails.budget;
      }
      if (
        mergedDetails.budgetAmount !== undefined &&
        mergedDetails.budgetAmount !== null
      ) {
        requestData.budgetAmount = Number(mergedDetails.budgetAmount);
      }

      const response = await axios.post(`${API}/api/generate-itinerary`, requestData);

      const mockMode = response.headers["x-mock-mode"] === "true";
      setIsMockMode(mockMode);
      setItinerary(response.data.itinerary);
      toast.dismiss();

      toast.success("¡Itinerario generado exitosamente!");

      // Navegar a la nueva página /ruta
      navigate("/ruta");
    } catch (error) {
      toast.dismiss();
      console.error("Error:", error);
      const status = error?.response?.status;
      const serverDetail = error?.response?.data?.detail;
      if (status === 503) {
        toast.error(
          serverDetail ||
            "El servicio está saturado. Espera unos segundos y vuelve a intentarlo.",
        );
      } else if (status === 429) {
        toast.error("Demasiadas peticiones. Prueba otra vez en un momento.");
      } else {
        toast.error("Error al generar el itinerario. Inténtalo de nuevo.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDetails = (details) => {
    setTravelDetails(details);
    toast.success("Detalles guardados correctamente");

    const mustVisit = (details?.mustVisit || "").trim();
    if (mustVisit) {
      axios
        .post(`${API}/api/insights/must-visit`, {
          text: mustVisit,
          destination: currentSearchData?.destination || "",
          user_id: user?.user_id || null,
        })
        .catch(() => {
          /* noop */
        });
    }
  };

  const handleAutoSearch = (details) => {
    if (!currentSearchData?.destination || !currentSearchData?.startDate || !currentSearchData?.endDate) return;
    setTravelDetails(details);
    handleSearch({
      destination: currentSearchData.destination,
      startDate: currentSearchData.startDate,
      endDate: currentSearchData.endDate,
      _preloadedDetails: details,
    });
  };

  const totalDays = currentSearchData.startDate && currentSearchData.endDate
    ? calculateDays(currentSearchData.startDate, currentSearchData.endDate)
    : 0;

  return (
    <div className="App min-h-screen" style={{ backgroundColor: "#FFFFFF" }}>
      <Toaster position="top-center" richColors />

      {isLoading && <ItineraryLoading />}

      {FEATURES.PLUS_ENABLED && <WelcomePromoModal />}

      <Header />

      {/* Hero con Buscador */}
      <HeroItinerary
        onSearch={handleSearch}
        onOpenDetails={() => setIsModalOpen(true)}
        onSearchDataChange={handleSearchDataChange}
      />

      {/* Modal de Detalles */}
      <TravelDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDetails}
        onAutoSearch={handleAutoSearch}
        totalDays={totalDays}
        destination={currentSearchData.destination || ""}
        startDate={currentSearchData.startDate || ""}
        endDate={currentSearchData.endDate || ""}
        userPlan={userPlan}
        isAuthenticated={isAuthenticated}
        onOpenAuth={() => window.dispatchEvent(new Event("visitalo:open-auth"))}
      />

      <Footer />
    </div>
  );
}

// Renderiza nada: solo monta el hook de analytics dentro de BrowserRouter.
function AnalyticsTracker() {
  useAnalytics();
  return null;
}

export default App;
